import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import ExcelSvg from '@components/ExcelSvg';
import WordSvg from '@components/WordSvg';
import { BookmarkedHadithsResult } from '@components/bookmarks/BookmarkedHadithsResult';
import { SelectBookmarkFoldersController } from '@components/bookmarks/SelectBookmarkFoldersController';
import ExportToWordModal from '@components/modal/Modal/ExportToWordModal';

import {
    BookmarkProvider,
    useBookmarkContext,
} from '@contexts/useBookmarkContext';
import { IFolder, useBookmarks } from '@hooks/useBookmarks';
import useTranslation from '@hooks/useTranslation';
import { useUI } from '@hooks/useUI';
import { PageLayout } from '@layout';
import { FolderCopy } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    FormControlLabel,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import { exportAndDownloadExcel } from '@utils/export';

import { analytics } from 'lib/analytics';

export default function Bookmarks() {
    const { isMobileOrTablet } = useUI();
    const router = useRouter();
    const [foldersDrawerOpen, setFoldersDrawerOpen] = useState(false);
    const { getStorageFolders, getFolder } = useBookmarks();
    const [folder, setFolder] = useState<IFolder | null>(null);
    const { t } = useTranslation('library');
    const { t: tBookmark } = useTranslation('bookmark');

    useEffect(() => {
        if (!router.isReady) return;

        (async () => {
            const folders = await getStorageFolders();
            if (!router.query.folder) {
                if (folders.length > 0) {
                    router.push(`/bookmarks?folder=${folders[0].uuid}`);
                }
            }

            if (router.query.folder) {
                if (folders.length === 0) {
                    router.push('/bookmarks');
                    return;
                }

                getFolder(router.query.folder as string).then(setFolder);
            }
        })();
    }, [router.query.folder]);

    return (
        <BookmarkProvider>
            <PageLayout>
                <Paper
                    elevation={0}
                    variant={isMobileOrTablet ? 'elevation' : 'outlined'}
                    sx={{
                        px: 2,
                        py: { sm: 2 },
                        borderRadius: '10px',
                        mt: { xs: 2, sm: 0 },
                    }}
                >
                    <ImportExportBookmarks />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'justify-between',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Alert
                            severity="warning"
                            sx={{
                                textAlign: {
                                    xs: 'center',
                                },
                                flexGrow: 1,
                            }}
                        >
                            {tBookmark('disclaimer')}
                        </Alert>
                        {!isMobileOrTablet && <ExportButtons />}
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: '3fr 9fr',
                            },
                            gap: 2,
                        }}
                    >
                        {!isMobileOrTablet && (
                            <SelectBookmarkFoldersController />
                        )}
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: !isMobileOrTablet
                                        ? 'flex-end'
                                        : 'space-between',
                                    gap: 2,
                                }}
                            >
                                {isMobileOrTablet && (
                                    <Box
                                        sx={{
                                            mb: { xs: 2, md: 0 },
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}
                                    >
                                        <Button
                                            color="secondary"
                                            onClick={() =>
                                                setFoldersDrawerOpen(true)
                                            }
                                            variant="outlined"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                borderRadius: '15px',
                                            }}
                                        >
                                            <FolderCopy />
                                            <Typography
                                                sx={{
                                                    maxWidth: 100,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    '@media(min-width: 480px)':
                                                        {
                                                            maxWidth: 250,
                                                        },
                                                }}
                                            >
                                                {folder?.name}
                                            </Typography>
                                        </Button>
                                        <ExportButtons />
                                    </Box>
                                )}
                            </Box>
                            <BookmarkedHadithsResult />
                        </Box>
                    </Box>
                </Paper>
            </PageLayout>
            <FoldersDrawer
                state={foldersDrawerOpen}
                onClose={() => setFoldersDrawerOpen(false)}
            >
                <SelectBookmarkFoldersController />
            </FoldersDrawer>
        </BookmarkProvider>
    );
}

function ExportButtons() {
    const { isMobileOrTablet } = useUI();
    const { t } = useTranslation('bookmark');
    const { hadiths } = useBookmarkContext();
    const [wordConfigOpen, setWordConfigOpen] = useState(false);
    function handleExportExcel() {
        if (!hadiths.length) {
            return alert(t('no_bookmarks_found'));
        }
        analytics('excel-export', { source: 'bookmarks' });

        const columns = {
            keys: [
                'book_name',
                'hadith_serial_id',
                'page',
                'volume',
                'chapter',
                'sub_chapter',
                'narrators_full_names',
                'matn_with_tashkeel',
                'matn',
            ],
            titles: [
                'المصنف',
                'رقم الحديث',
                'الصفحة',
                'الجزء',
                'الكتاب',
                'الباب',
                'الرواة',
                'المتن',
                'المتن بالتشكيل',
            ],
            options: [
                { maxWidth: 35 },
                { maxWidth: 35 },
                { maxWidth: 35 },
                { maxWidth: 35 },
                { maxWidth: 35 },
                { maxWidth: 35 },
                { maxWidth: 35 },
                { width: 300 },
                { width: 300 },
            ],
        };

        const DOWNLOAD_NAME = 'المنصة_الحديثية_نتائج_المفضلة';

        exportAndDownloadExcel({
            data: hadiths,
            sheetName: DOWNLOAD_NAME,
            columns,
        });
    }

    if (!hadiths.length) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                }}
            >
                <IconButton
                    disabled={isMobileOrTablet}
                    onClick={handleExportExcel}
                >
                    <ExcelSvg
                        width={24}
                        height={24}
                        disabled={isMobileOrTablet}
                    />
                </IconButton>
                <IconButton
                    disabled={isMobileOrTablet}
                    onClick={() => setWordConfigOpen(true)}
                >
                    <WordSvg
                        width={24}
                        height={24}
                        disabled={isMobileOrTablet}
                    />
                </IconButton>
            </Box>
            {wordConfigOpen && (
                <ExportToWordModal
                    handleClose={() => setWordConfigOpen(false)}
                    hadiths={hadiths}
                />
            )}
        </>
    );
}

export function ImportExportBookmarks() {
    const { ImportAndInsertJson, exportFoldersToJSON, importAndReplace } =
        useBookmarks();
    const { setFolders, folders } = useBookmarkContext();
    const router = useRouter();
    const { t } = useTranslation('bookmark');
    const { isMobileOrTablet } = useUI();
    const [importJsonModalOpen, setImportJsonModalOpen] = useState(false);
    const jsonFileRef = useRef<File | null>(null);
    const IMPORT_JSON_INPUT = 'import-json-input';
    async function importJson() {
        try {
            const file = await new Promise<File | null>((resolve) => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.id = IMPORT_JSON_INPUT;
                input.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (files && files.length > 0) {
                        resolve(files[0]);
                    } else {
                        resolve(null);
                    }
                };
                input.click();
            });

            if (!file) {
                return;
            }

            jsonFileRef.current = file;
            setImportJsonModalOpen(true);
        } catch (error) {
            console.error('Error importing JSON:', error);
            alert('Failed to import');
        }
    }

    async function submitImportJson(replace: boolean) {
        try {
            if (!jsonFileRef.current) {
                throw new Error('No file selected');
            }

            const formData = new FormData();
            formData.append('file', jsonFileRef.current);

            const response = await (replace
                ? importAndReplace
                : ImportAndInsertJson)(formData);

            if (!response) {
                throw new Error('No folders found in JSON');
            }
            setFolders(response);

            if (!router.query.folder && response.length > 0) {
                router.push(`/bookmarks?folder=${response[0].uuid}`);
            }
        } catch (error) {
            console.error('Error importing JSON:', error);
            alert('Failed to import');
        } finally {
            const input = document.getElementById(IMPORT_JSON_INPUT);
            if (input) {
                input.remove();
            }
            setImportJsonModalOpen(false);
        }
    }

    if (isMobileOrTablet) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Button variant="outlined" onClick={importJson}>
                    {t('import_json_modal_import')}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => exportFoldersToJSON(folders)}
                    disabled={!folders.length}
                >
                    {t('json_modal_export')}
                </Button>
            </Box>

            {importJsonModalOpen && (
                <ImportJsonModal
                    handleClose={() => setImportJsonModalOpen(false)}
                    submit={submitImportJson}
                />
            )}
        </>
    );
}

function FoldersDrawer({
    state,
    onClose,
    children,
}: {
    state: boolean;
    onClose: () => void;
    children: React.ReactNode;
}) {
    return (
        <Drawer anchor="bottom" open={state} onClose={onClose}>
            {children}
        </Drawer>
    );
}

function ImportJsonModal({
    handleClose,
    submit,
}: {
    handleClose: () => void;
    submit: (replace: boolean) => void;
}) {
    const { t } = useTranslation('bookmark');
    const [replace, setReplace] = useState(false);
    return (
        <Dialog onClose={handleClose} open={true}>
            <DialogTitle>{t('import_json_modal_title')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t('import_json_modal_description')}
                </DialogContentText>
                <Box>
                    <FormControlLabel
                        sx={{
                            display: 'flex',
                            maxWidth: '100%',
                        }}
                        label={t(
                            'import_json_modal_replace_existing_bookmarks',
                        )}
                        control={
                            <Checkbox
                                size="small"
                                checked={replace}
                                onChange={(event) => {
                                    setReplace(event.target.checked);
                                }}
                            />
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {t('import_json_modal_cancel')}
                </Button>
                <Button onClick={() => submit(replace)}>
                    {t('import_json_modal_import')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
